import { QUESTIONNAIRES } from "@/config/questionnaires.data";

/** Text in one or more languages; questionnaires may be Norwegian-only. */
export type QuestionnaireText = Partial<Record<string, string>>;

export type QuestionnaireFieldType = "text" | "textarea" | "radio" | "number" | "checkbox";

export interface QuestionnaireOption {
  value: string;
  label: QuestionnaireText;
}

/** Shows a field only when another field has a value (radio) or includes one (checkbox). */
export interface QuestionnaireCondition {
  field: string;
  value?: string;
  includes?: string;
}

export interface QuestionnaireField {
  id: string;
  key: string;
  type: QuestionnaireFieldType;
  label: QuestionnaireText;
  options?: QuestionnaireOption[];
  visibleWhen?: QuestionnaireCondition;
}

export interface QuestionnaireQuestion {
  /** Question number such as "II.4"; separators have none. */
  number?: string;
  title: QuestionnaireText;
  type?: "separator";
  helpText?: QuestionnaireText;
  fields?: QuestionnaireField[];
}

export type PublicMapping =
  | { target: string; source: string; transform: "copy" }
  | { target: string; source: string; transform: "optionLabels"; removeParentheses?: boolean }
  | {
      target: string;
      source: string;
      transform: "classification";
      values: Record<string, string>;
      multiple: string;
    }
  | { target: string; source: string; transform: "minimumAge" | "maximumAge" }
  | { target: string; sources: string[]; transform: "urls" };

export interface QuestionnaireDefinition {
  id: string;
  version: number;
  publicMapping?: PublicMapping[];
  heading: QuestionnaireText;
  introduction: QuestionnaireText;
  matrixMetadataHeading?: QuestionnaireText;
  questions: QuestionnaireQuestion[];
}

export type AnswerValue = string | number | string[] | null;
export type Answers = Record<string, AnswerValue | undefined>;

/** Stored private response (`intervention_responses`). */
export interface QuestionnaireResponse {
  questionnaire_id: string;
  questionnaire_version: number;
  answers: Answers;
}

export class UnknownQuestionnaireError extends Error {}

export function findQuestionnaire(
  id: string,
  version: number,
): QuestionnaireDefinition | undefined {
  return QUESTIONNAIRES.find((config) => config.id === id && config.version === version);
}

export function getQuestionnaire(id: string, version: number): QuestionnaireDefinition {
  const config = findQuestionnaire(id, version);
  if (!config) throw new UnknownQuestionnaireError(`${id} v${version}`);
  return config;
}

export function getQuestionnaireText(
  value: QuestionnaireText | string | undefined,
  language: string,
): string {
  if (value && typeof value === "object") return value[language] || value.no || value.en || "";
  return value ?? "";
}

export function getAllFields(config: QuestionnaireDefinition): QuestionnaireField[] {
  return config.questions.flatMap((question) => question.fields ?? []);
}

function getEmptyValue(field: QuestionnaireField): AnswerValue {
  if (field.type === "checkbox") return [];
  if (field.type === "number") return null;
  return "";
}

export function getFieldValue(answers: Answers, field: QuestionnaireField): AnswerValue {
  const value = answers[field.key];
  return value === undefined || value === null ? getEmptyValue(field) : value;
}

function conditionMatches(
  value: AnswerValue | undefined,
  condition: QuestionnaireCondition,
): boolean {
  const selected = Array.isArray(value) ? value.map(String) : [String(value ?? "")];
  return condition.includes !== undefined
    ? selected.includes(condition.includes)
    : selected.includes(condition.value ?? "");
}

export function isFieldVisible(field: QuestionnaireField, answers: Answers): boolean {
  return (
    !field.visibleWhen || conditionMatches(answers[field.visibleWhen.field], field.visibleWhen)
  );
}

function isAnswered(value: AnswerValue | undefined): boolean {
  return (
    value !== undefined &&
    value !== null &&
    value !== "" &&
    !(Array.isArray(value) && value.length === 0)
  );
}

function getFieldDisplayValue(
  field: QuestionnaireField,
  value: AnswerValue | undefined,
  language: string,
): string {
  const labelFor = (entry: string) => {
    const option = field.options?.find((candidate) => candidate.value === entry);
    return option ? getQuestionnaireText(option.label, language) : entry;
  };
  if (Array.isArray(value)) return value.map(labelFor).join(", ");
  if (value == null) return "";
  return field.options ? labelFor(String(value)) : String(value);
}

export interface AnsweredQuestion {
  question: QuestionnaireQuestion;
  fields: { field: QuestionnaireField; display: string }[];
}

/** Questions with at least one visible, answered field (read-only summary). */
export function getAnsweredQuestions(
  config: QuestionnaireDefinition,
  answers: Answers,
  language: string,
): AnsweredQuestion[] {
  return config.questions
    .filter((question) => question.type !== "separator")
    .map((question) => ({
      question,
      fields: (question.fields ?? [])
        .filter((field) => isAnswered(answers[field.key]) && isFieldVisible(field, answers))
        .map((field) => ({
          field,
          display: getFieldDisplayValue(field, answers[field.key], language),
        })),
    }))
    .filter((entry) => entry.fields.length > 0);
}

/** Normalizes form state into the stored answer shape, keeping unknown keys. */
export function normalizeAnswers(
  config: QuestionnaireDefinition,
  existing: Answers,
  draft: Answers,
): Answers {
  const answers: Answers = { ...existing };
  for (const field of getAllFields(config)) {
    const value = draft[field.key];
    let normalized: AnswerValue;
    if (field.type === "checkbox") {
      normalized = Array.isArray(value) ? value.map(String) : [];
    } else if (field.type === "number") {
      const text = value == null ? "" : String(value).trim();
      normalized = text === "" ? null : Number(text);
    } else {
      normalized = value == null ? "" : String(value).trim();
    }

    // Preserve old data and intentional clearing, but don't manufacture an
    // empty answer for every unanswered question on each save. In particular,
    // a previously missing key must not turn into "", null or [].
    const empty =
      normalized === null ||
      normalized === "" ||
      (Array.isArray(normalized) && normalized.length === 0);
    if (empty && !Object.prototype.hasOwnProperty.call(existing, field.key)) {
      continue;
    }
    answers[field.key] = normalized;
  }
  return answers;
}

function stripParentheses(label: string): string {
  let result = label;
  // Repeated passes also remove nested groups such as "(... (WHO) ...)".
  while (/\([^()]*\)/.test(result)) result = result.replace(/\([^()]*\)/g, "");
  return result;
}

function extractUrls(text: string): string[] {
  return (text.match(/(?:https?:\/\/|www\.)[^\s<>"';]+/gi) ?? []).map((match) => {
    let url = match.replace(/[.,!?:]+$/, "");
    // Keep balanced parentheses inside URLs, drop a trailing prose ")".
    while (url.endsWith(")") && (url.match(/\)/g) ?? []).length > (url.match(/\(/g) ?? []).length) {
      url = url.slice(0, -1);
    }
    return url.replace(/[\]},.!?:]+$/, "");
  });
}

/**
 * Derives public intervention fields (title, description, objectives,
 * classification, ages, links) from private answers using the
 * questionnaire's `publicMapping`. Option labels use the UI language; age
 * ranges are always read from the Norwegian label ("(6-12 år)").
 */
export function getPublicValues(
  config: QuestionnaireDefinition,
  answers: Answers,
  language: string,
): Record<string, string> {
  const fields = getAllFields(config);
  // Answers to hidden fields (e.g. ages when no age range is chosen) are not published.
  const answerFor = (key: string) => {
    const field = fields.find((candidate) => candidate.key === key);
    return !field || isFieldVisible(field, answers) ? answers[key] : undefined;
  };
  const result: Record<string, string> = {};
  for (const mapping of config.publicMapping ?? []) {
    if (mapping.transform === "urls") {
      const urls = mapping.sources.flatMap((key) => extractUrls(String(answerFor(key) ?? "")));
      result[mapping.target] = [
        ...new Set(urls.map((url) => (/^www\./i.test(url) ? `https://${url}` : url))),
      ].join(";");
      continue;
    }
    const value = answerFor(mapping.source);
    const selected = Array.isArray(value) ? value : [];
    const options = (fields.find((field) => field.key === mapping.source)?.options ?? []).filter(
      (option) => selected.includes(option.value),
    );
    switch (mapping.transform) {
      case "copy":
        result[mapping.target] = value == null ? "" : String(value);
        break;
      case "optionLabels":
        result[mapping.target] = options
          .map((option) => {
            const label = getQuestionnaireText(option.label, language);
            return (mapping.removeParentheses ? stripParentheses(label) : label)
              .replace(/\s+/g, " ")
              .trim();
          })
          .join(";");
        break;
      case "classification": {
        const codes = options
          .map((option) => mapping.values[option.value])
          .filter((code) => code !== undefined);
        result[mapping.target] = options.length > 1 ? mapping.multiple : (codes[0] ?? "");
        break;
      }
      case "minimumAge":
      case "maximumAge": {
        const ranges = options.flatMap((option) => {
          const match = getQuestionnaireText(option.label, "no").match(
            /\((\d+)\s*[-–—]\s*(\d+)\s*år\)/,
          );
          return match ? [[Number(match[1]), Number(match[2])] as const] : [];
        });
        result[mapping.target] = !ranges.length
          ? ""
          : String(
              mapping.transform === "minimumAge"
                ? Math.min(...ranges.map((r) => r[0]))
                : Math.max(...ranges.map((r) => r[1])),
            );
        break;
      }
    }
  }
  return result;
}

/** True when the draft contains any meaningful (non-empty) answer. */
export function hasAnyAnswer(answers: Answers): boolean {
  return Object.values(answers).some((value) =>
    typeof value === "string" ? value.trim() !== "" : isAnswered(value),
  );
}

/** Whether a response has anything to show: answers, or an unknown questionnaire version. */
export function hasResponseContent(
  response: QuestionnaireResponse | null,
  language: string,
): boolean {
  if (!response) return false;
  const config = findQuestionnaire(response.questionnaire_id, response.questionnaire_version);
  return !config || getAnsweredQuestions(config, response.answers ?? {}, language).length > 0;
}
